import { EventMessage, EventType, IPublicClientApplication } from "@azure/msal-browser";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from "@azure/msal-react";
import * as React from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { CustomNavigationClient } from "./utils/NavigationClient";

import DarkModeSwitch, { useDarkMode } from "./components/DarkModeSwitch";
import Home from "./routes/Home";

import { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ErrorBoundary from "./components/ErrorBoundary";
import SkeletonWrapper from "./components/SkeltonWrapper";
import Splash from "./routes/Splash";
import { EntityActionProvider } from "./components/EntityActionContext";
import Profile from "./routes/Profile";
import { EntitiesProvider } from "./components/EntitiesContext";

type AppProps = {
    pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    const [isDarkMode, toggleDarkMode] = useDarkMode();

    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        pca.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.HANDLE_REDIRECT_END) {
                setIsLoading(false);
            }
        });
    }, []);

    return (
        <MsalProvider instance={pca}>
            <div className={isDarkMode ? "dark" : ""}>
                <SkeletonTheme
                    baseColor="transparent"
                    highlightColor="var(--color-brand)"
                >
                    <SkeletonWrapper loading={isLoading}>
                        <div className="relative">
                            <DarkModeSwitch />
                            <div
                                className="min-h-screen w-full flex justify-center bg-background"
                                style={{
                                    backgroundImage: isDarkMode
                                        ? "url(/images/mc-world-night.png)"
                                        : "url(/images/mc-world-day.png)",
                                    backgroundSize: "cover",
                                }}
                            >
                                <div className="absolute inset-0 w-full">
                                    <ErrorBoundary>
                                        <EntitiesProvider>
                                            <EntityActionProvider>
                                                <AuthenticatedTemplate>
                                                    <Routes>
                                                        <Route path="/profile" element={<Profile />} />
                                                        <Route path="/*" element={<Home />} />
                                                    </Routes>
                                                </AuthenticatedTemplate>
                                                <UnauthenticatedTemplate>
                                                    <Routes>
                                                        <Route path="/*" element={<Splash />} />
                                                    </Routes>
                                                </UnauthenticatedTemplate>
                                            </EntityActionProvider>
                                        </EntitiesProvider>
                                    </ErrorBoundary>
                                </div>
                            </div>
                        </div>
                    </SkeletonWrapper>
                </SkeletonTheme>
            </div>
        </MsalProvider>
    );
}

export default App;
